/**
 * RESPONSIBILITY: which sections make up the Sermons pages, and in what order.
 *
 *   #sermons            the head, the search and filters over the sermons, YouTube's
 *                       newest not yet entered, and the way to the channel for older ones
 *   #sermons/<sermon>   that sermon's own page, its pencil opening just that sermon
 *
 * A sermon switched off is not shown to a visitor; whoever may edit sees it, marked —
 * the same rule as events (R37).
 */
import { SectionName, type SermonRow } from '@app/sections';
import { idOf, rowById } from '@framework/core';
import { SectionUpdate } from '../cms';
import {
  Crumbs, PageHead, SermonFinder, SermonsYoutube, SermonsArchive, SermonDetail,
  bySlugOrId, useWords,
} from '../site';

const visibleTo = (canEdit: boolean, list: SermonRow[] | undefined): SermonRow[] =>
  (list ?? []).filter((s) => canEdit || s.show !== false);

export function SermonsPage({ path = [] }: { path?: readonly string[] }) {
  const w = useWords();

  if (path[0]) {
    return (
      <main>
        <SectionUpdate name={SectionName.Sermons} pencil="none">
          {(c, edit) => {
            const all = visibleTo(edit.canEdit, c.sermons);
            const s = bySlugOrId(all, path[0]);
            return (
              <SectionUpdate
                name={SectionName.Sermons}
                focus={s ? { field: 'sermons', rowId: idOf(s) } : undefined}
              >
                {(fresh) => <SermonDetail c={fresh} s={s && (rowById(fresh.sermons ?? [], idOf(s)) ?? s)} all={all} />}
              </SectionUpdate>
            );
          }}
        </SectionUpdate>
      </main>
    );
  }

  return (
    <main>
      <Crumbs trail={[{ label: w.sermonsLabel }]} />

      <SectionUpdate name={SectionName.SermonsHero}>
        {(c) => <PageHead eyebrow={c.eyebrow} heading={c.heading} intro={c.intro} />}
      </SectionUpdate>

      <SectionUpdate name={SectionName.Sermons}>
        {(c, edit) => {
          const sermons = visibleTo(edit.canEdit, c.sermons);
          return (
            <>
              <SermonFinder c={c} sermons={sermons} />
              {/* The visible ones only: a sermon switched off while its notes are written
                  must not take last Sunday's video off the page with it. */}
              <SermonsYoutube c={c} sermons={sermons} />
            </>
          );
        }}
      </SectionUpdate>

      <SectionUpdate name={SectionName.SermonsArchive}>
        {(c) => <SermonsArchive c={c} />}
      </SectionUpdate>
    </main>
  );
}
